import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { useContract } from '@/hooks/useContract';
import useSend from '@/hooks/useSend';
import user from '@/stores/user';

import { ChainId, NETWORK_CONFIG } from '@/constant';
import lazbubuAbi from '@/contracts/abi/lazbubu.json';
import serverConfig from '@/stores/serverConfig';
import ErrorIcon from '@mui/icons-material/Error';
import {
    Alert,
    Box,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import { isAddress } from 'ethers';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
interface TransferDatFormData {
    receiverAddress: string;
}

interface TransferDatProps {
    isOpen: boolean;
    onClose: () => void;
    datId: number;
    name: string;
    avatar: string;
    datAddress: string;
    onConfirm: (receiverAddress: string) => void;
    onTransferSuccess?: () => void;
}

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    max-width: 480px;
`;

const AssetSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px 0;
`;

const AssetImage = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 12px;
    object-fit: cover;
`;

const AssetName = styled(Typography)`
    font-size: 20px;
    font-weight: 600;
    color: #000;
    text-align: center;
`;

const DatAddressTag = styled.div`
    background: #f5f5f5;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 14px;
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    user-select: all;
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const AddressLabel = styled(Typography)`
    font-size: 16px;
    font-weight: 500;
    color: #000;
`;

const WarningBox = styled(Alert)`
    border-radius: 12px;
    background: rgba(235, 239, 254, 1);
    padding: 16px 12px;

    color: #000;

    .MuiAlert-icon {
        color: #000;
        svg {
            width: 24px;
            height: 24px;
        }
    }
`;

const TransferDatModal: React.FC<TransferDatProps> = observer(
    ({
        isOpen,
        onClose,
        datId,
        name,
        avatar,
        datAddress,
        onConfirm,
        onTransferSuccess,
    }) => {
        const [isTransferring, setIsTransferring] = useState(false);
        const [submitError, setSubmitError] = useState<string>('');
        const nftContract = useContract(
            serverConfig.config?.contracts.dataAnchoringTokenAddress,
            lazbubuAbi,
        );
        const send = useSend();
        const {
            control,
            handleSubmit,
            formState: { errors },
            reset,
        } = useForm<TransferDatFormData>({
            defaultValues: {
                receiverAddress: '',
            },
        });

        const onSubmit = async (data: TransferDatFormData) => {
            // clear previous error before new submit
            setSubmitError('');
            if (!isAddress(data.receiverAddress)) {
                return;
            }

            setIsTransferring(true);
            try {
                await send({
                    contract: nftContract,
                    method: 'safeTransferFrom',
                    params: [
                        user.address,
                        data.receiverAddress,
                        datId,
                        '1',
                        '0x',
                    ],
                    onSuccess: () => {
                        setSubmitError('');
                        onTransferSuccess?.();
                        reset();
                        onClose();
                    },
                    onError: (e) => {
                        if (e?.code !== 'ACTION_REJECTED') {
                            setSubmitError(
                                e?.shortMessage ||
                                    e?.message ||
                                    'Transaction failed. Please try again.',
                            );
                        }
                        setIsTransferring(false);
                    },
                });
            } catch (error) {
                console.error('Transfer failed:', error);
                const e = error as any;
                if (e?.code !== 'ACTION_REJECTED') {
                    setSubmitError(
                        e?.shortMessage ||
                            e?.message ||
                            'Transaction failed. Please try again.',
                    );
                }
            } finally {
                setIsTransferring(false);
            }
        };

        const handleClose = () => {
            if (!isTransferring) {
                reset();
                onClose();
            }
        };

        const onClickDat = () => {
            const explorer =
                NETWORK_CONFIG[user.chainId || ChainId.testnet].explore;
            window.open(`${explorer}address/${datAddress}`, '_blank');
        };

        return (
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title="Transfer DAT"
                hideClose={isTransferring}
            >
                <ModalContent>
                    <AssetSection>
                        <AssetImage src={avatar} alt={name} />
                        <AssetName variant="h5">{name}</AssetName>
                        <DatAddressTag onClick={onClickDat}>
                            Token Id: {datId}
                        </DatAddressTag>
                    </AssetSection>

                    <FormSection>
                        <AddressLabel>Address</AddressLabel>

                        <Controller
                            name="receiverAddress"
                            control={control}
                            rules={{
                                required: 'Address is required',
                                validate: (value) => {
                                    if (!value) return 'Address is required';
                                    if (!isAddress(value))
                                        return 'Invalid Ethereum address';
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    placeholder="e.g. 0x186458..."
                                    variant="outlined"
                                    fullWidth
                                    error={!!errors.receiverAddress}
                                    helperText={errors.receiverAddress?.message}
                                    disabled={isTransferring}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            height: '56px',
                                            '& fieldset': {
                                                borderColor:
                                                    errors.receiverAddress
                                                        ? '#d32f2f'
                                                        : '#e0e0e0',
                                            },
                                            '&:hover fieldset': {
                                                borderColor:
                                                    errors.receiverAddress
                                                        ? '#d32f2f'
                                                        : '#bdbdbd',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor:
                                                    errors.receiverAddress
                                                        ? '#d32f2f'
                                                        : '#000',
                                            },
                                        },
                                    }}
                                />
                            )}
                        />

                        <WarningBox icon={<ErrorIcon />} severity="warning">
                            Items sent to the wrong address cannot be recovered
                        </WarningBox>
                    </FormSection>

                    {submitError && (
                        <Alert severity="error" sx={{ borderRadius: '12px' }}>
                            {submitError}
                        </Alert>
                    )}

                    <Button
                        size="normal"
                        type="primary"
                        disabled={isTransferring || !!errors.receiverAddress}
                        onClick={handleSubmit(onSubmit)}
                        showLoading={false}
                        style={{ width: '100%' }}
                    >
                        {isTransferring ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={20} color="inherit" />
                                Transferring...
                            </Box>
                        ) : (
                            'Transfer'
                        )}
                    </Button>
                </ModalContent>
            </Modal>
        );
    },
);

export default TransferDatModal;
